import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import { Box } from "@mui/material";
import {
  getBuilderViews,
  saveBuilderView,
  deleteBuilderView,
} from "../../../../apis/apiEQL";

const MAX_VIEWS = 20;

/**
 * Top bar: save the current pivot as a named view, and load / delete saved
 * ones. Persisted per user on the dedicated builder-views endpoint (see
 * apiEQL), so it never mixes with reports' saved filters.
 *
 * @param {() => Object} getState - snapshot of the current Builder state to save
 * @param {(state: Object) => void} onLoad - restore a saved view's `state`
 */
export const SavedViewsBar = ({ getState, onLoad }) => {
  const [t] = useTranslation("exports");
  const [views, setViews] = useState([]);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  const reload = async () => {
    try {
      const resp = await getBuilderViews();
      setViews(resp?.data || []);
    } catch (e) {
      console.error("Builder: could not load saved views", e);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  // Close the dropdown on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (views.length >= MAX_VIEWS) {
      enqueueSnackbar(t("builder.views_limit", { max: MAX_VIEWS }), {
        variant: "warning",
        preventDuplicate: true,
      });
      return;
    }
    setSaving(true);
    try {
      await saveBuilderView(trimmed, getState());
      setName("");
      await reload();
      enqueueSnackbar(t("builder.view_saved"), { variant: "success" });
    } catch (e) {
      console.error("Builder: could not save view", e);
      enqueueSnackbar(t("builder.view_save_error"), { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleLoad = (view) => {
    setOpen(false);
    if (view?.state) onLoad(view.state);
  };

  const handleDelete = async (e, viewId) => {
    e.stopPropagation();
    try {
      await deleteBuilderView(viewId);
      await reload();
    } catch (err) {
      console.error("Builder: could not delete view", err);
      enqueueSnackbar(t("builder.view_delete_error"), { variant: "error" });
    }
  };

  return (
    <Box className="builder-savedbar dyTheme1 dyBorder1 rounded">
      <input
        className="builder-savedbar__input form-control"
        placeholder={t("builder.view_name_placeholder")}
        value={name}
        maxLength={100}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && handleSave()}
      />
      <button
        type="button"
        className="builder-btn builder-btn--primary builder-btn--compact"
        onClick={handleSave}
        disabled={saving || !name.trim()}
      >
        {t("builder.save_view")}
      </button>

      <div className="builder-savedbar__menu" ref={menuRef}>
        <button
          type="button"
          className="builder-btn builder-btn--ghost builder-btn--compact"
          onClick={() => setOpen((v) => !v)}
        >
          {t("builder.saved_views")} ({views.length})
        </button>
        {open && (
          <div className="builder-savedbar__dropdown dyTheme1 dyBorder1 rounded">
            {views.length === 0 ? (
              <div className="builder-savedbar__empty">
                {t("builder.no_saved_views")}
              </div>
            ) : (
              views.map((v) => (
                <button
                  key={v.view_id}
                  type="button"
                  className="builder-savedbar__item"
                  onClick={() => handleLoad(v)}
                >
                  <span className="builder-savedbar__item-name">{v.name}</span>
                  <span
                    className="builder-savedbar__item-del"
                    role="button"
                    tabIndex={0}
                    aria-label={t("builder.delete_view")}
                    onClick={(e) => handleDelete(e, v.view_id)}
                  >
                    ×
                  </span>
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </Box>
  );
};
